import { useMemo } from "react";
import { useTranslation } from "react-i18next";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import {
  agentToolsApi,
  type MCPConnectionDTO,
  type PaginatedMCPConnectionsResponse,
} from "../../../services/agent-tools-service";
import { mapConnectionDtoToUi } from "../adapters";
import type { MCPConnection } from "../types";
import { MCP_CONNECTIONS_QUERY_KEY, mcpQueryKeys } from "../query-keys";
import { useMCPQueryScope, mcpRequestOptions, type MCPQueryContext } from "./useMCPQueryScope";

export const MCP_CONNECTION_DETAIL_QUERY_KEY = [
  ...MCP_CONNECTIONS_QUERY_KEY,
  "detail",
] as const;

type UseMCPConnectionResult = {
  connection: MCPConnection | null;
  dto: MCPConnectionDTO | undefined;
  hasAuthoritativeData: boolean;
  isLoading: boolean;
  isFetching: boolean;
  error: Error | null;
};

// Seed only from this exact principal/workspace/project selection's list pages.
const useListSeed = (id: string | null, scope: MCPQueryContext): MCPConnectionDTO | undefined => {
  const queryClient = useQueryClient();
  return useMemo(() => {
    if (!id || !scope.enabled) return undefined;
    const caches = queryClient.getQueriesData<PaginatedMCPConnectionsResponse>({
      queryKey: mcpQueryKeys.lists(scope.key),
    });
    for (const [key, data] of caches) {
      // Keep list data separate from detail/tools/test cache shapes.
      if (key[2] !== "list") continue;
      const items = data?.items;
      if (!Array.isArray(items)) continue;
      const match = items.find((item) => item.id === id);
      if (match) return match;
    }
    return undefined;
  }, [id, queryClient, scope]);
};

export const useMCPConnection = (
  id: string | null,
): UseMCPConnectionResult => {
  const { i18n } = useTranslation();
  const locale = i18n.resolvedLanguage ?? i18n.language;
  const scope = useMCPQueryScope();
  const seed = useListSeed(id, scope);

  const query = useQuery({
    queryKey: mcpQueryKeys.detail(scope.key, id),
    queryFn: ({ signal }) => agentToolsApi.getMCPConnection(id as string, mcpRequestOptions(scope, signal)),
    enabled: scope.enabled && Boolean(id),
    initialData: seed,
  });

  const connection = useMemo(
    () => (query.data ? mapConnectionDtoToUi(query.data, locale) : null),
    [locale, query.data],
  );

  return {
    connection,
    dto: query.data,
    hasAuthoritativeData:
      query.isFetched && !query.isFetching && !query.isError,
    isLoading: query.isLoading,
    isFetching: query.isFetching,
    error: query.error as Error | null,
  };
};
